/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { defaultRichTextObjectPM, defaultTOCRichTextObjectPM } from "cms/components/components/defaults";
import { AccordionComponent } from "cms/components/components/accordion";
import { CallToActionComponent } from "cms/components/components/call-to-action";
import { CardsComponent } from "cms/components/components/cards";
import { CardsSimpleComponent } from "cms/components/components/cards-simple";
import { DividerComponent } from "cms/components/components/divider";
import { DocumentListComponent } from "cms/components/components/document-list";
import { EmbedComponent } from "cms/components/components/embed";
import { FeatureWebpageComponent } from "cms/components/components/feature-webpage";
import { FeedbackComponent } from "cms/components/components/feedback";
import { FeedModelComponent } from "cms/components/components/feed-model";
import { FeedCalendarComponent } from "cms/components/components/feed-calendar";
import { FormsComponent } from "cms/components/components/forms";
import { HeadingComponent } from "cms/components/components/heading";
import { HeroCarouselComponent } from "cms/components/components/hero-carousel";
import { HeroComponent } from "cms/components/components/hero";
import { HighlightComponent } from "cms/components/components/highlight";
import { IconLinkListComponent } from "cms/components/components/icon-links-list";
import { InfoBoxComponent } from "cms/components/components/info-box";
import { ImageComponent } from "cms/components/components/image";
import { ImageListComponent } from "cms/components/components/image-list";
import { ImageLinkComponent } from "cms/components/components/image-link";
import { ImageGalleryComponent } from "cms/components/components/image-gallery";
import { LinkButtonComponent } from "cms/components/components/link-button";
import { LinksListComponent } from "cms/components/components/links-list";
import { PermitSelectComponent } from "cms/components/components/permit-select";
import { PermitListAuthComponent } from "cms/components/components/permit-list-auth";
import { PermitListComponent } from "cms/components/components/permit-list";
import { RichTextEditorTipTapComponent } from "cms/components/components/rich-text-editor-tiptap";
import { RichtextWithImageComponent } from "cms/components/components/richtext-wrap-with-image";
import { ScrollRevealImageComponent } from "cms/components/components/scroll-reveal";
import { NavigationListComponent } from "cms/components/components/navigation-list";
import { TimelineComponent } from "cms/components/components/timeline";
import { TabGroupComponent } from "cms/components/components/tab-group";
import { YoutubeComponent } from "cms/components/components/youtube";
import { ZencityComponent } from "cms/components/components/zencity";
import { TestimonialComponent } from "cms/components/components/testimonial";
import { RoutewareCalendarComponent } from "cms/components/components/routeware-calendar";
import { RoutewareWizardComponent } from "cms/components/components/routeware-wizard";
import { CityVizComponent } from "cms/components/components/cityviz";
import { TocComponent } from "cms/components/components/toc";
import { LINK_FORMATS, LINK_STYLES } from "cms/links/link/helpers";
import { DOCUMENT_TARGETS, DOCUMENT_STYLES, DOCUMENT_FORMATS, DOCUMENT_LIST } from "cms/documents/document/helpers";

// ---------------------------------------------------------
// Customer Components
// ---------------------------------------------------------
import { DNVLoginComponent } from "cms/components/external/dnv/login-button";

// ---------------------------------------------------------
// Langley Components
// ---------------------------------------------------------
import { HeroSlideshowComponent } from "cms/components/components/hero-slideshow";
import { IconLinkTabsComponent } from "cms/components/components/icon-link-tabs";
import { NewsAndEventsFeedComponent } from "cms/components/components/news-events-feed";
import { IconCTAComponent } from "cms/components/components/icon-cta";
import { NavCardsComponent } from "cms/components/components/nav-cards";
import { NavCardsSimple } from "cms/components/components/nav-cards-simple";

// New components for SimpliCity website
import { ButtonCTAComponent } from "cms/components/components/button-cta";
import { FeatureAccordionComponent } from "cms/components/components/feature-accordion";
import { NewsletterCTAComponent } from "cms/components/components/newsletter-cta";
import { TestimonialHighlightComponent } from "cms/components/components/testimonial-highlight";
import { ContactCardComponent } from "./contact-card";
import { DataTableComponent } from "./data-table";
import { ProcessCardsComponent } from "./process-cards";
import { SectionLinkCardsComponent } from "./section-link-cards";

// New components for Kitimat website
import { NewsHighlightsComponent } from "./news-highlights";
import { EventsCouncilFeedComponent } from "./events-council-feed";
import { CardComponent } from "./card";
import { BannerComponent } from "./banner";

/**
 * componentTypes
 * @type {[SimpliCityComponentConfig]}
 */
export const componentTypes = [
    //  Group: Text (Rich Text, Heading, Rich Text with Image, Quote, Magic)
    {
        type: "component.rich-text-editor",
        component: RichTextEditorTipTapComponent,
        defaultImpl: {
            richtext: defaultRichTextObjectPM,
        },
    },
    {
        type: "component.rich-text",
        component: RichTextEditorTipTapComponent,
        defaultImpl: {
            richtext: defaultRichTextObjectPM,
        },
    },
    {
        type: "component.rich-text-with-image",
        component: RichtextWithImageComponent,
        defaultImpl: {
            richtext: defaultRichTextObjectPM,
            image: null,
            side: "left"
        },
    },
    {
        type: "component.scroll-reveal-image",
        component: ScrollRevealImageComponent,
        defaultImpl: {
            richtext: defaultRichTextObjectPM,
            image: null,
        },
    },
    {
        type: "component.highlight",
        component: HighlightComponent,
        defaultImpl: {
            description: defaultRichTextObjectPM,
            image: null,
            section_name: "",
            subtitle: "",
            title: "",
            image_links: []
        },
    },
    {
        type: "component.dnv.login-signup-button",
        component: DNVLoginComponent,
        defaultImpl: {},
    },
    {
        type: "component.heading",
        component: HeadingComponent,
        defaultImpl: {
            style: "heading2"
        },
    },
    {
        type: "component.info-box",
        component: InfoBoxComponent,
        defaultImpl: {
            richtext: defaultRichTextObjectPM,
            style: "",
        },
    },
    {
        type: "component.divider",
        component: DividerComponent,
        defaultImpl: {},
        hideLabel: true,
    },
    //  Group: Assets  (Image, Image Card, Gallery, Youtube, Document)
    {
        type: "component.hero-carousel",
        component: HeroCarouselComponent,
        defaultImpl: {
            slides: [],
        },
    },
    {
        type: "component.dnv.hero-carousel",
        component: HeroCarouselComponent,
        defaultImpl: {
            slides: [],
        },
    },
    {
        type: "component.cards",
        component: CardsComponent,
        defaultImpl: {
            card_list: [],
        },
    },
    {
        type: "component.hero",
        component: HeroComponent,
        defaultImpl: {
            slides: [],
        },
    },
    {
        type: "component.image",
        component: ImageComponent,
        defaultImpl: {
            image: null,
            settings: {
                position_type: "background cover",
                position: "center center",
            },
        },
    },
    {
        type: "component.image-gallery",
        component: ImageGalleryComponent,
        defaultImpl: {
            guid: "",
            title: "",
            galleryType: "thumbnail",
            description: defaultRichTextObjectPM,
            image_gallery: [],
        },
    },
    {
        type: "component.image-list",
        component: ImageListComponent,
        defaultImpl: {
            title: "",
            description: defaultRichTextObjectPM,
            image_list: [],
        },
    },
    {
        type: "component.testimonials",
        component: TestimonialComponent,
        defaultImpl: {
            title: "",
            description: defaultRichTextObjectPM,
            image_list: [],
        },
    },
    {
        type: "component.cards-simple",
        component: CardsSimpleComponent,
        defaultImpl: {
            title: "",
            description: defaultRichTextObjectPM,
            image_list: [],
        },
    },
    {
        type: "component.image-link",
        component: ImageLinkComponent,
        defaultImpl: {
            title: "",
            description: defaultRichTextObjectPM,
            image_list: [],
        },
    },
    {
        type: "component.cards",
        component: CardsComponent,
        defaultImpl: {
            title: "",
            description: defaultRichTextObjectPM,
            image_list: [],
        },
    },
    {
        type: "component.youtube",
        component: YoutubeComponent,
        defaultImpl: {
            useEmbed: false,
            forceResponsive: true,
            embed: "<!-- SimpliCity CMS Youtube placeholder -->\n",
            title: "",
            srcid: "",
            src: "",
            width: "100%",
            height: "540",
        },
    },

    // Langley components
    {
        type: "component.hero-slideshow",
        component: HeroSlideshowComponent,
    },
    {
        type: "component.icon-link-tabs",
        component: IconLinkTabsComponent,
    },
    {
        type: "component.news-events-feed",
        component: NewsAndEventsFeedComponent,
    },
    {
        type: "component.icon-cta",
        component: IconCTAComponent,
    },
    {
        type: "component.nav-cards",
        component: NavCardsComponent,
    },
    {
        type: "component.nav-cards-simple",
        component: NavCardsSimple,
    },

    //  Group: Collections (Accordion, Tab Group, Image List, Document List, Table)
    {
        type: "component.accordion",
        component: AccordionComponent,
        defaultImpl: {
            title: "",
            description: "",
            sections: [],
            showSteps: false
        },
    },
    {
        type: "component.tab-panel",
        component: TabGroupComponent,
        defaultImpl: {
            tabs: [],
        },
    },
    {
        type: "component.feature-webpage",
        component: FeatureWebpageComponent,
        defaultImpl: {
            title: "",
            webpages: [],
            webpageCard: "fixed",
        },
    },
    {
        type: "component.document-list",
        component: DocumentListComponent,
        defaultImpl: {
            title: "",
            documents: [],
            listStyle: DOCUMENT_LIST.ICON,
            settings: {
                style: DOCUMENT_STYLES.BODY,
                format: DOCUMENT_FORMATS.TITLE_SIZE,
                target: DOCUMENT_TARGETS.NEW,
            },
        },
    },
    //  Group: Navigation (Links List, Link Button, Navigation List)
    {
        type: "component.links-list",
        component: LinksListComponent,
        defaultImpl: {
            title: "",
            links: [],
            settings: {
                style: LINK_STYLES.BODY,
                format: LINK_FORMATS.UNDERLINE,
            },
        },
    },
    {
        type: "component.link-button",
        component: LinkButtonComponent,
        defaultImpl: {
            link: "",
            link_text: "",
            style: "default",
        },
    },
    {
        type: "component.call-to-action",
        component: CallToActionComponent,
        defaultImpl: {
            title: "",
            description: "",
            image: "",
            imageCard: "horizontal",
            links: [],
            settings: {
                style: LINK_STYLES.BODY,
                format: LINK_FORMATS.TEXT,
            },
        },
    },
    {
        type: "component.navigation-list",
        component: NavigationListComponent,
        defaultImpl: {
            description: defaultRichTextObjectPM,
        },
    },
    {
        type: "component.timeline",
        component: TimelineComponent,
        defaultImpl: {
            description: defaultRichTextObjectPM,
        },
    },
    {
        type: "component.table-of-contents",
        component: TocComponent,
        defaultImpl: {
            richtext: defaultTOCRichTextObjectPM,
        },
    },

    {
        type: "component.icon-links-list",
        component: IconLinkListComponent,
        defaultImpl: {
            title: "",
            icon_links: [],
            settings: {
                style: LINK_STYLES.BODY,
                format: LINK_FORMATS.TEXT,
            },
        },
    },

    //  Group: Input (Forms, Feedback, Embed)
    {
        type: "component.form",
        component: FormsComponent,
        defaultImpl: {
            form_id: "",
            title: "",
            status: "draft",
            formType: "request",
            version: "1.0.0",
            steps: [],
            questions: [],
            questionsLength: [],
            reuses: 0,
        },
    },
    {
        type: "component.feedback",
        component: FeedbackComponent,
        defaultImpl: {
            primary_question: "",
        },
    },
    {
        type: "component.embed",
        component: EmbedComponent,
        defaultImpl: {
            html: "<!-- SimpliCity CMS Embed placeholder -->\n",
            isResponsive: false,
            _enableScript: false,
        },
    },
    {
        type: "component.feed-model",
        component: FeedModelComponent,
        defaultImpl: {
            selected_type: "model.page",
            enable_bar: false,
        },
    },
    {
        type: "component.feed-calendar",
        component: FeedCalendarComponent,
        defaultImpl: {
            selected_type: "model.event",
            selected_category: "",
            enable_bar: false,
        },
    },
    {
        type: "component.civilspace-engagement",
        component: ZencityComponent,
        defaultImpl: {
            project: "",
            topic: "",
        },
    },
    {
        type: "component.routeware-calendar",
        component: RoutewareCalendarComponent,
        defaultImpl: {
            area: "",
            name: "calendar",
        },
    },
    {
        type: "component.routeware-wizard",
        component: RoutewareWizardComponent,
        defaultImpl: {
            area: "",
            name: "wizard",
        },
    },
    {
        type: "component.permit-list-auth",
        component: PermitListAuthComponent,
        defaultImpl: {
            selected_type: "housing",
            show_summary: true,
        },
    },
    {
        type: "component.permit-list",
        component: PermitListComponent,
        defaultImpl: {
            selected_type: "housing",
            show_summary: true,
        },
    },
    {
        type: "component.permit-select",
        component: PermitSelectComponent,
        defaultImpl: {
            selected_type: "housing"
        },
    },
    {
        type: "MAGIC",
        component: null,
        defaultImpl: {},
    },

    // New components for SimpliCity website and Kitimat
    {
        type: "component.button-cta",
        component: ButtonCTAComponent,
    },
    {
        type: "component.feature-accordion",
        component: FeatureAccordionComponent,
    },
    {
        type: "component.newsletter-cta",
        component: NewsletterCTAComponent,
    },
    {
        type: "component.testimonial-highlight",
        component: TestimonialHighlightComponent,
    },
    {
        type: "component.contact-card",
        component: ContactCardComponent,
    },
    {
        type: "component.data-table",
        component: DataTableComponent,
    },
    {
        type: "component.process-cards",
        component: ProcessCardsComponent,
    },
    {
        type: "component.section-link-cards",
        component: SectionLinkCardsComponent,
    },
    {
        type: "component.news-highlights",
        component: NewsHighlightsComponent,
    },
    {
        type: "component.events-council-feed",
        component: EventsCouncilFeedComponent,
    },
    {
        type: "component.card",
        component: CardComponent,
    },
    {
        type: "component.banner",
        component: BannerComponent,
    },
    {
        type: "component.cityviz",
        component: CityVizComponent,
    },
];
